import { setCookie, getCookie } from 'utils/cookie';
import { isObject, slugify } from 'utils';
import {
  matchPageRoute,
  getPageRoute,
  redirectToCheckout,
} from 'utils/storefront';
import {
  postEasyblocksCanvasPageRoute,
  postStorefrontCanvasReload,
  postStorefrontNotifyError,
} from 'components/storefront/editor/utils/post-message';
import { STOREFRONT_EDITOR_CANVAS_PATH } from 'components/storefront/editor/utils/editor';

/** @typedef {import('@swell/apps-sdk').SwellTheme} SwellTheme */

const CLIENT_DOMAIN_REGEX = new RegExp(process.env.CLIENT_DOMAIN_PATTERN);

function isScriptLoaded(src, content) {
  let existingScript;

  if (src) {
    existingScript = document.querySelector(`script[src="${src}"]`);
  } else {
    existingScript = Array.from(document.querySelectorAll('script')).find(
      (script) => script.innerHTML.trim() === content.trim(),
    );
  }

  return existingScript ? true : false;
}

/**
 * @param {string} [src]
 * @param {string} [content]
 * @param {string} [type]
 * @returns {(() => Promise<void>) | void}
 */
export function loadScript({ src, content, type, defer, async }) {
  if (!src && !content) {
    return;
  }

  if (isScriptLoaded(src, content)) {
    return;
  }

  const loader = (options) => {
    if (isScriptLoaded(src, content)) {
      return Promise.resolve();
    }

    const script = document.createElement('script');

    if (type) {
      script.setAttribute('type', type);
    }

    if (src) {
      script.src = src;
      script.defer = defer;
      script.async = async ?? false;
    } else {
      script.innerHTML = content;
    }

    document.head.appendChild(script);

    if (!src) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      script.onload = () => {
        if (options?.scriptManager) {
          options.scriptManager.onScriptLoad();
        }

        resolve();
      };
      script.onerror = (event) => {
        reject(new Error(event.toString()));
      };
    });
  };

  return loader;
}

function getLoadedStyle(hash, content) {
  let existingStyle;

  if (hash) {
    existingStyle = document.head.querySelector(`style[data-hash="${hash}"]`);
  } else if (content) {
    existingStyle = Array.from(document.querySelectorAll('style')).find(
      (style) => style.innerHTML.trim() === content.trim(),
    );
  }

  return existingStyle ?? null;
}

/**
 * @param {string} [hash]
 * @param {string} [content]
 * @returns {(() => Promise<void>) | void}
 */
export function loadStyle(hash, content) {
  if (!hash && !content) {
    return;
  }

  const existingStyle = getLoadedStyle(hash, content);

  if (!existingStyle) {
    const loader = () => {
      if (getLoadedStyle(hash, content)) {
        return Promise.resolve();
      }

      const style = document.createElement('style');

      if (hash) {
        style.setAttribute('data-hash', hash);
      }

      style.innerHTML = content;

      document.head.appendChild(style);

      return Promise.resolve();
    };

    return loader;
  }

  if (hash) {
    existingStyle.innerHTML = content;
  }
}

function isStylesheetLoaded(href) {
  return document.querySelector(`link[href="${href}"]`) !== null;
}

export function loadStylesheet(href) {
  if (!href) {
    return;
  }

  if (isStylesheetLoaded(href)) {
    return;
  }

  return () => {
    if (isStylesheetLoaded(href)) {
      return Promise.resolve();
    }

    const link = document.createElement('link');

    link.rel = 'stylesheet';
    link.href = href;

    document.head.appendChild(link);

    return new Promise((resolve, reject) => {
      link.onload = resolve;
      link.onerror = (event) => {
        reject(new Error(event.toString()));
      };
    });
  };
}

/**
 * @param {string} href
 * @param {SwellTheme} theme
 * @returns {[string, unknown][]}
 */
export function matchPageRouteWithAdapted(href, theme) {
  let { pageId, recordId } = matchPageRoute(href, theme.props.pages);
  if (!pageId) {
    const adaptedPageUrl = theme.resolveUrlSetting(href);
    if (adaptedPageUrl !== href) {
      const adaptedResult = matchPageRoute(adaptedPageUrl, theme.props.pages);
      ({ pageId, recordId } = adaptedResult);
    }
  }

  return { pageId, recordId };
}

/**
 * checks if href is external
 * @param {string} href
 * @returns {boolean}
 */
export function isExternalUrl(href) {
  const url = new URL(href);
  const currentUrl = new URL(window.parent.location.href);
  return url.hostname !== currentUrl.hostname;
}

/**
 * checks if href is checkout url
 * @param {string} href
 * @returns {boolean}
 */
export function isCheckoutUrl(href) {
  if (!href) {
    return false;
  }

  return href.includes('/checkout/');
}

const LINK_REGEX = /^https?:\/\//i;

/**
 * @param {SwellTheme} theme
 * @param {string} href
 * @param {string | object} prevStyle - current node style
 * @returns {[string, unknown][]}
 */
export function handleLink(theme, href, prevStyle, form) {
  if (LINK_REGEX.test(href)) {
    if (isExternalUrl(href)) {
      return [
        [
          'onclick',
          function onClickLink(ev) {
            ev.preventDefault();
            window.parent.open(href, '_blank').focus();
          },
        ],
      ];
    }

    // checkout links
    if (theme.shopifyCompatibility?.isCheckoutForm(form)) {
      return [
        [
          'onclick',
          function onClickLink() {
            // open checkout page in the editor
            redirectToCheckout(href);
          },
        ],
      ];
    }

    // previous style can be either a string (directly use it) or an object with all styles in React notation (convert to string)
    let newStyle = typeof prevStyle === 'string' ? prevStyle : '';
    // convert React object to string
    if (isObject(prevStyle)) {
      for (const key of Object.keys(prevStyle)) {
        newStyle = `${newStyle}${newStyle ? '; ' : ''}${slugify(key)}: ${
          prevStyle[key]
        }`;
      }
    }

    // add editor styles
    newStyle = `${newStyle}${
      newStyle ? '; ' : ''
    }pointer-events: none; cursor: default;`;

    return [['style', newStyle]];
  }

  const isAnchor = href?.startsWith('#');

  if (!href || isAnchor) {
    return [];
  }

  const { pageId } = matchPageRouteWithAdapted(href, theme);

  if (pageId) {
    return [];
  }

  // threat links without pageId as form actions
  const formAction = href !== '#' && !pageId;

  return [
    ['href', '#'],
    [
      'onclick',
      function onClickLink() {
        if (formAction) {
          // form action
          formActionHandler(theme, '', href, 'get');
        }
      },
    ],
  ];
}

/**
 * @param {SwellTheme} theme
 * @param {string} [form]
 * @param {string} [type]
 * @returns {[string, unknown][]}
 */
export function handleButton(theme, form, type) {
  if (!form) {
    return [];
  }

  const action = `/${form}`;
  const isFormSubmitButton =
    type === 'submit' &&
    form &&
    theme.shopifyCompatibility?.isScriptFormActionUrl(action);

  if (!isFormSubmitButton) {
    return [];
  }

  return [
    [
      'onclick',
      function onClickButton(event) {
        event.preventDefault();

        const body = new FormData(event.target?.form);

        formActionHandler(theme, form, action, 'post', body);
      },
    ],
  ];
}

/**
 * @param {SwellTheme} theme
 * @param {string} [action]
 * @returns {[string, unknown][]}
 */
export function handleForm(theme, action) {
  if (!action) {
    return [];
  }

  const themeForm = (theme?.forms || []).find(
    (themeForm) => themeForm.url === action,
  );

  if (!themeForm) {
    return [];
  }

  return [
    [
      'onsubmit',
      function onSubmitForm(event) {
        event.preventDefault();
        const eventForm = event.target;

        // do not duplicate script actions
        if (theme.shopifyCompatibility?.isScriptFormActionUrl(action)) {
          return;
        }

        const body = new FormData(eventForm);

        formActionHandler(theme, themeForm.id, action, 'post', body, eventForm);
      },
    ],
  ];
}

/**
 * @param {SwellTheme} theme
 * @param {string} formId
 * @param {Response} res
 * @returns {Promise<Response | undefined>}
 */
export async function handleFormActionResponse(theme, formId, res) {
  try {
    const result = await res.clone().json();
    const { redirect, swellData, status } = result;
    let shouldReloadPage = false;

    theme.clearFormData();

    if (swellData) {
      // update cookies
      console.log('handleFormActionResponse', swellData);

      setCookie('swell-data', swellData || {});

      const session = swellData['swell-session'] || '';
      const locale = swellData['swell-locale'];
      const currency = swellData['swell-currency'];

      setCookie('swell-session', session);

      if (locale && locale !== getCookie('swell-locale')) {
        setCookie('swell-locale', locale);
        shouldReloadPage = true;
      }

      if (currency && currency !== getCookie('swell-currency')) {
        setCookie('swell-currency', currency);
        shouldReloadPage = true;
      }

      // use actual formData
      const swellFormData = swellData['swell-form-data'];

      if (swellFormData) {
        try {
          const data = JSON.parse(swellFormData);
          const formData = data?.[formId];
          theme.setFormData(formId, formData);
        } catch {
          // none
        }
      }
    }

    // we should reload the same page if status is 307
    if (shouldReloadPage || (redirect && status === 307)) {
      return postStorefrontCanvasReload(window.parent);
    }

    let pageId = '';
    if (redirect) {
      if (theme.shopifyCompatibility?.isCheckoutForm(formId)) {
        // open checkouts with different hostnames in a separate tab, we cannot show them in the editor
        if (isExternalUrl(redirect)) {
          window.parent.open(redirect, '_blank').focus();
          return;
        }

        // open checkout page in the editor
        redirectToCheckout(redirect);
        return res;
      }

      // redirect in editor
      const { pageId: id } = matchPageRoute(redirect, theme.props.pages) || '';
      pageId = id;
    }

    // reload current page
    if (!pageId || theme.pageId === pageId) {
      // the same page: refresh page with updated globals
      window.setThemeGlobals();
      if (theme.shopifyCompatibility?.needRedirectToPageStart(formId)) {
        window.location.href = '#';
        return res;
      }
    } else {
      // show page
      postEasyblocksCanvasPageRoute(pageId, window.parent);
    }

    return res;
  } catch (err) {
    postStorefrontNotifyError('Unable to perform form action', window.parent);
  }
}

/**
 * @param {SwellTheme} theme
 * @param {string} formId
 * @param {string} action
 * @param {string} method
 * @param {BodyInit} [body]
 * @param {HTMLFormElement} [eventForm]
 */
export async function formActionHandler(
  theme,
  formId,
  action,
  method,
  body,
  eventForm,
) {
  try {
    const res = await fetch(action, {
      method,
      body: method === 'post' || method === 'put' ? body : undefined,
    });

    // we always clear forms in Proxima
    if (eventForm) {
      eventForm.reset();
    }

    return handleFormActionResponse(theme, formId, res);
  } catch (err) {
    postStorefrontNotifyError('Unable to perform form action', window.parent);
  }
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit} [options]
 * @returns {[string, RequestInit]}
 */
function parseFetchArgs(input, options) {
  if (input instanceof Request) {
    return [input.url, input];
  }

  return [input.toString(), options || {}];
}

/**
 * replaces STOREFRONT_EDITOR_CANVAS_PATH with the actual page
 * example: /admin/test/storefronts/HzptZpzz/storefront-editor-canvas?section_id=page__collection__main_collection-product-grid&filter_price%5Bgte%5D=&filter_price%5Blte%5D=&sort_by=name_asc
 * @param {SwellTheme} theme
 * @param {string} url
 * @returns {string}
 */
function replaceStoreFrontEditorCanvas(theme, url) {
  if (!url.includes(STOREFRONT_EDITOR_CANVAS_PATH)) {
    return url;
  }

  const search = window.parent?.location?.search || '';
  const params = new URLSearchParams(search);
  const pageId = params.get('page') || '';
  const recordId = params.get('record') || '';
  if (!pageId) {
    return url;
  }

  const replaced = getPageRoute(theme.props.pages, pageId, recordId);
  if (!replaced) {
    return url;
  }

  const parts = url.split('?');
  const query = parts.length > 1 ? parts[1] : '';
  return `${replaced}?${query}`;
}

/**
 * @param {object} options
 * @param {boolean} addIsEditor
 * @returns {void}
 */
function addSwellHeaders(options, addIsEditor = true) {
  options.headers = new Headers(options.headers);

  options.headers.set(
    'Swell-Data',
    JSON.stringify(getCookie('swell-data') || {}),
  );

  const session = getCookie('swell-session');
  if (session) {
    options.headers.set('X-Session', session);
  }
  if (addIsEditor) {
    options.headers.set('Swell-Is-Editor', true);
  }
}

/**
 * @param {SwellTheme} theme
 * @returns {void}
 */
export function initGlobalHandlers(theme, record) {
  if (window.globalHandlersAssigned) {
    return;
  }

  const origin = theme?.swell?.storefront_url || window.location.origin;

  // redefine global fetch function to handle shopify script actions
  const handler = window.fetch;
  window.fetch = function fetch(...args) {
    const [originalUrl, options] = parseFetchArgs(...args);
    // Some themes (Dawn) use window.location.href, which should be replaced with the actual page
    const origUrl = replaceStoreFrontEditorCanvas(theme, originalUrl);
    const url = new URL(origUrl, origin);

    if (
      (url.origin !== origin && !CLIENT_DOMAIN_REGEX.test(url.hostname)) ||
      originalUrl !== origUrl
    ) {
      // need to add swell session headers for relative urls
      if (originalUrl.startsWith('/')) {
        addSwellHeaders(options, false);
      }
      return handler(url, options);
    }

    // add editor headers
    addSwellHeaders(options);

    return handler(url, options).then((res) => {
      if (theme.shopifyCompatibility?.isScriptFormActionUrl(origUrl)) {
        // apply response
        return handleFormActionResponse(theme, '', res);
      }

      return res;
    });
  };

  // assign global form submit handler to handle this.submit();
  function formSubmitHandler() {
    const action = this.getAttribute('action');
    const method = this.getAttribute('method');
    const body = new FormData(this);
    formActionHandler(theme, action, action, method, body);
  }
  HTMLFormElement.prototype.submit = formSubmitHandler;

  // assign global HTMX configRequest handler to configure requests before sending
  document.addEventListener('htmx:configRequest', (event) => {
    const url = new URL(event.detail.path, origin);

    event.detail.path = url.href;
    const session = getCookie('swell-session');
    if (session) {
      event.detail.headers['X-Session'] = session;
    }
  });

  window.globalHandlersAssigned = true;
}

/**
 * @param {SwellTheme} theme
 * @returns {void}
 */
export function initAjaxHandlers(theme) {
  if (window.ajaxHandlersAssigned || !window.jQuery) {
    return;
  }

  const origin = theme?.swell?.storefront_url || window.location.origin;

  // Intercept all AJAX requests before they are sent
  if (window.jQuery.ajaxPrefilter) {
    window.jQuery.ajaxPrefilter((options) => {
      const url = new URL(options.url, origin);
      const session = getCookie('swell-session');
      const swellData = getCookie('swell-data');

      options.headers = options.headers || {};
      options.headers['Swell-Data'] = JSON.stringify(swellData || {});
      options.headers['Swell-Is-Editor'] = true;

      if (session) {
        options.headers['X-Session'] = session;
      }

      options.url = url.href;
    });
  }

  // Listen to all successful AJAX responses
  window
    .jQuery(document)
    .on('ajaxSuccess', (event, jqXHR, settings, result) => {
      if (!result?.swellData) {
        return;
      }

      const { swellData } = result;
      const session = swellData['swell-session'] || '';

      setCookie('swell-data', swellData || {});
      setCookie('swell-session', session);
    });

  window.ajaxHandlersAssigned = true;
}
